import { useEffect, useRef } from 'react';

import { endpoints } from '~/api/endpoints';
import { refreshAuthToken } from '~/api/refresh-token';
import {
  createNotificationsStream,
  type NotificationStreamEvent,
} from '~/features/notifications/services/notifications-sse';
import { useAuthStore } from '~/store/auth.store';

interface Options {
  /** Called for each new notification event. */
  onNotification: (event: NotificationStreamEvent) => void;
  /**
   * Gates the stream. When `false` (e.g. in-app channel disabled in
   * preferences) the stream stays closed; flipping it tears down or opens the
   * connection. Defaults to `true`.
   */
  enabled?: boolean;
  /**
   * Fired once when the stream exhausts its reconnect budget (generic network
   * failures or repeated 429s) and gives up for this session. Consumers
   * surface this (e.g. a toast). A fresh mount — page reload, app boot, new
   * window — resets the budget and retries automatically.
   */
  onUnavailable?: () => void;
}

/** Reconnect backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped). */
const MAX_RECONNECT_DELAY_MS = 30_000;
const BASE_RECONNECT_DELAY_MS = 1000;
/**
 * Max *consecutive* generic (non-429) reconnect attempts before giving up for
 * the session. Prevents the stream from retrying forever against a service
 * that's down. Counter resets when a connection stays open past
 * `MIN_STABLE_CONNECTION_MS`, so a healthy stream that later blips gets a fresh
 * budget. A page reload / new window also resets it (fresh effect run).
 */
const MAX_RECONNECT_ATTEMPTS = 5;
/**
 * A connection must stay open at least this long before its close is treated
 * as "stable". Only stable connections reset the backoff counter — this
 * prevents fast-cycling streams (open → close in < 10 s) from resetting
 * `attempts` to 0 and turning the reconnect loop into an infinite rapid loop.
 */
const MIN_STABLE_CONNECTION_MS = 10_000;

/** 429 Too Many Requests: ~2s default retry (with jitter), capped at 5 *consecutive* attempts. Counter resets on a successful onOpen. */
const RATE_LIMIT_RETRY_DELAY_MS = 2000;
const RATE_LIMIT_MAX_ATTEMPTS = 5;
/** Upper bound on Retry-After to keep a misconfigured server from parking the stream for hours. */
const RATE_LIMIT_RETRY_AFTER_CAP_MS = 60_000;

/** Duck-typed guard for upstream 429 — survives module-graph duplication where `instanceof` would fail. */
function isRateLimitError(err: unknown): err is { status: 429; retryAfterSeconds?: number } {
  return typeof err === 'object' && err !== null && (err as { status?: number }).status === 429;
}

/**
 * Duck-typed guard for an auth failure on the stream. The platform gateway
 * answers an expired access token with 401 OR 403 (see AGENT-259), so both
 * trigger a single token refresh + reconnect.
 */
function isAuthError(err: unknown): boolean {
  const status = (err as { status?: number }).status;
  return status === 401 || status === 403;
}

/**
 * ±50% jitter — prevents thundering-herd reconnects. Without it, every client
 * disconnected at the same moment (gateway blip, rate-limit trip) retries on
 * the same schedule and pile-drives the server all over again.
 */
function jitter(baseMs: number): number {
  return baseMs * (0.5 + Math.random());
}

/**
 * Owns the SSE lifecycle for the notifications stream:
 *   - Opens when authenticated AND a tenant (subsidiary) is active AND
 *     `enabled` (caller gates this on the in-app channel being on).
 *   - Rebinds when `activeTenantId` changes (per project rule: stream is
 *     subsidiary-scoped — recipient filtering inside the subsidiary is
 *     handled server-side).
 *   - Reconnects with exponential backoff on network errors / clean closes,
 *     capped at MAX_RECONNECT_ATTEMPTS (and RATE_LIMIT_MAX_ATTEMPTS for 429s);
 *     on exhaustion it fires `onUnavailable` once and stops. A fresh mount
 *     (reload / app boot / new window) resets the budget and retries.
 *   - Cleans up on unmount, logout, tenant switch, or `enabled` → false.
 *
 * The provided `onNotification` is captured via a ref so reconnects don't
 * tear down whenever the parent component re-renders.
 */
export function useNotificationsStream({
  onNotification,
  enabled = true,
  onUnavailable,
}: Options): void {
  // Boolean presence, not the token value: a *refresh* (token changes, presence
  // unchanged) must NOT tear down a healthy stream — the SSE service reads the
  // live token from the store at each connect. Only presence flips (login /
  // logout) open or close the connection.
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  const activeTenantId = useAuthStore((s) => s.user?.activeTenantId);

  const handlerRef = useRef(onNotification);
  const unavailableRef = useRef(onUnavailable);

  useEffect(() => {
    handlerRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    unavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  useEffect(() => {
    if (!isAuthed || !activeTenantId || !enabled) return;

    let cancelled = false;
    let attempts = 0;
    let rateLimitAttempts = 0;
    let gaveUp = false;
    let controller: AbortController | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    /** Timestamp (ms) when the current stream received its first successful 200, or null if not yet open. */
    let streamOpenedAt: number | null = null;
    /**
     * Whether the one-shot token refresh has been spent for this connect cycle.
     * Reset on a successful open — the SSE analogue of the interceptor's
     * `X-Auth-Retry` guard, so a refreshed-but-still-rejected token can't loop
     * refreshes forever.
     */
    let authRefreshed = false;

    /** Stop retrying for this session and notify the consumer exactly once. */
    function giveUp(reason: string): void {
      if (gaveUp) return;
      gaveUp = true;
      console.warn(`[sse] notifications stream gave up: ${reason}`);
      unavailableRef.current?.();
    }

    // Generic reconnect path (network/close/non-429 errors) — exponential
    // backoff, capped at MAX_RECONNECT_ATTEMPTS consecutive tries.
    function scheduleReconnect(): void {
      if (cancelled) return;
      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        giveUp(`${String(MAX_RECONNECT_ATTEMPTS)} consecutive reconnect attempts failed`);
        return;
      }
      attempts += 1;
      const base = Math.min(
        MAX_RECONNECT_DELAY_MS,
        BASE_RECONNECT_DELAY_MS * 2 ** Math.min(attempts - 1, 5),
      );
      reconnectTimeout = setTimeout(connect, jitter(base));
    }

    // 429-specific path — honors Retry-After when present (clamped), falls back to ~2s with jitter.
    // Cap is on *consecutive* 429s within a single connect cycle; counter resets on a successful onOpen.
    function scheduleRateLimitedReconnect(retryAfterSeconds: number | undefined): void {
      if (cancelled) return;
      rateLimitAttempts += 1;
      if (rateLimitAttempts > RATE_LIMIT_MAX_ATTEMPTS) {
        giveUp(`${String(RATE_LIMIT_MAX_ATTEMPTS)} consecutive 429 retries`);
        return;
      }
      // Prefer the server's hint (clamped to a sane ceiling) so we don't fight the rate limiter.
      const delay =
        retryAfterSeconds !== undefined
          ? Math.min(retryAfterSeconds * 1000, RATE_LIMIT_RETRY_AFTER_CAP_MS)
          : jitter(RATE_LIMIT_RETRY_DELAY_MS);
      reconnectTimeout = setTimeout(connect, delay);
    }

    function handleError(error: Error): void {
      if (isRateLimitError(error)) {
        scheduleRateLimitedReconnect(error.retryAfterSeconds);
        return;
      }
      // Expired/again-rejected token (gateway uses 401 or 403): refresh once and
      // reconnect with the new token. The SSE path is a raw fetch, so — unlike
      // ky calls — nothing else refreshes it; without this it just retried with
      // the stale token until the budget ran out and toasted (AGENT-260).
      if (isAuthError(error) && !authRefreshed) {
        authRefreshed = true;
        void refreshAuthToken().then((outcome) => {
          if (cancelled) return;
          if (outcome === 'refreshed') {
            connect(); // immediate retry with the freshly-stored token
          } else if (outcome === 'transient') {
            scheduleReconnect(); // refresh hiccup — fall back to backoff
          }
          // 'invalid' → tokens cleared upstream; `isAuthed` flips false and this
          // effect tears down. Don't reconnect or toast — the ky interceptor
          // owns the /login bounce on the next request.
        });
        return;
      }
      scheduleReconnect();
    }

    function connect(): void {
      if (cancelled) return;
      controller = createNotificationsStream({
        url: endpoints.notifications.stream(),
        onOpen: () => {
          // Record when the stream opened. Backoff reset is deferred to onComplete
          // so only stable connections (open > MIN_STABLE_CONNECTION_MS) earn the reset.
          // Resetting here on every 200 OK caused an infinite rapid-reconnect loop when
          // the server opened and closed the stream within milliseconds.
          streamOpenedAt = Date.now();
          rateLimitAttempts = 0;
          // A successful open means the current token authenticated — re-arm the
          // one-shot refresh so a later mid-session expiry can refresh again.
          authRefreshed = false;
        },
        onNotification: (event) => {
          handlerRef.current(event);
        },
        onComplete: () => {
          // Only reset the backoff counter when the connection was genuinely stable.
          // Short-lived connections (< MIN_STABLE_CONNECTION_MS) keep the current
          // backoff so the delay keeps growing instead of resetting to ~750 ms.
          const wasStable =
            streamOpenedAt !== null && Date.now() - streamOpenedAt >= MIN_STABLE_CONNECTION_MS;
          streamOpenedAt = null;
          if (wasStable) attempts = 0;
          scheduleReconnect();
        },
        onError: (err) => {
          // A connection that was stable before erroring earns a fresh budget,
          // mirroring the clean-close path — otherwise earlier transient
          // failures could count against an otherwise-healthy stream.
          const wasStable =
            streamOpenedAt !== null && Date.now() - streamOpenedAt >= MIN_STABLE_CONNECTION_MS;
          streamOpenedAt = null;
          if (wasStable) attempts = 0;
          handleError(err);
        },
      });
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      controller?.abort();
    };
  }, [isAuthed, activeTenantId, enabled]);
}
